import React from 'react'
import classNames from 'classnames'
import kebabCase from 'lodash.kebabcase'
import { Text } from 'marketing-site/src/library/elements/Text'
import { IProps } from './index'
import { mq } from 'marketing-site/src/library/utils'
import { RichText } from 'marketing-site/src/mvp/library/elements/RichText'
import { CTALink } from 'marketing-site/src/mvp/library/elements/CTALink'
import { CTATheme } from 'marketing-site/src/mvp/library/utils'

export const PreFooterCTABlock = ({
  eyebrow,
  title,
  subhead,
  ctas,
  width = 'inset',
  backgroundColor = 'transparent',
  checkmarks,
  linkText,
}: IProps): React.ReactElement => {
  const classes = classNames('pre-footer', 'grid-layout', {
    [`mvp-background-color-${kebabCase(backgroundColor)}`]: backgroundColor,
  })

  const ctaColor =
    backgroundColor === 'blue' || backgroundColor === 'slate' || backgroundColor === 'deepTeal'
      ? CTATheme.LinkOnlyPaper
      : CTATheme.LinkOnlySlate

  return (
    <>
      <div className={classes}>
        <div className="pre-footer__inner">
          <div className="pre-footer__content">
            {eyebrow && (
              <div className="pre-footer__eyebrow">
                <Text size="eyebrow">{eyebrow}</Text>
              </div>
            )}
            <div className="pre-footer__title">
              <Text size="heading-3">{title}</Text>
            </div>
            {subhead && (
              <div className="pre-footer__subhead">
                <Text size="subhead-sm">{subhead}</Text>
              </div>
            )}
            {linkText && (
              <div className="pre-footer__link-text">
                <CTALink {...linkText} variant={ctaColor} />
              </div>
            )}
          </div>
          <div className="pre-footer__ctas">
            {!!ctas && ctas}
            {checkmarks && (
              <div className="pre-footer__checkmarks">
                <Text size="caption">
                  <RichText document={checkmarks} />
                </Text>
              </div>
            )}
          </div>
        </div>
      </div>

      <style jsx>
        {`
          .pre-footer {
            max-width: ${width === 'inset' ? '77.5rem' : 'unset'};
            padding-left: ${width === 'inset' ? '2rem' : '1.25rem'};
            padding-right: ${width === 'inset' ? '2rem' : '1.25rem'};
            margin-left: ${width === 'inset' ? '1.25rem' : '0'};
            margin-right: ${width === 'inset' ? '1.25rem' : '0'};
            border-radius: ${width === 'inset' ? '1.5rem' : '0'};

            &__inner {
              row-gap: 2rem;
              align-items: center;
            }

            &__content {
              text-align: center;
              grid-column: 1/-1;
            }

            &__eyebrow {
              margin-bottom: 1rem;
            }

            &__subhead {
              margin-top: 1rem;
            }

            &__link-text {
              margin-top: 1.5rem;
            }

            &__ctas {
              grid-column: 1/-1;
              display: flex;
              flex-direction: column;
              gap: ${ctas.length > 1 ? '0.75rem' : '1rem'};

              :global(.email-form__input) {
                box-shadow: inset 0 0 0 2px var(--neutral-200);
              }

              :global(.email-form--flex-column .email-form__submit) {
                margin-block-start: 0.5rem;
              }

              :global(.ul-list--checkmarks) {
                display: flex;
                flex-wrap: wrap;
                justify-content: center;
                column-gap: 1.5rem;
                row-gap: 0.5rem;

                :global(li) {
                  margin-bottom: 0;
                  align-items: center;

                  :global(svg) {
                    margin-top: 0;
                  }
                }
              }
            }

            &__checkmarks {
              width: 100%;

              :global(.rich-text ul),
              :global(.rich-text span),
              :global(.rich-text p) {
                font-size: inherit;
              }
            }
          }

          @media (${mq.tablet}) {
            .pre-footer {
              padding-left: ${width === 'inset' ? '0' : '1.25rem'};
              padding-right: ${width === 'inset' ? '0' : '1.25rem'};

              &__inner {
                row-gap: 2.5rem;
              }

              &__content {
                grid-column: 2/-2;
              }

              &__eyebrow {
                margin-bottom: 1.25rem;
              }

              &__ctas {
                grid-column: 2/-2;
                flex-direction: ${ctas.length > 1 ? 'row' : 'column'};
                justify-content: center;

                :global(.ul-list--checkmarks) {
                  :global(li:last-child) {
                    margin-top: 0;
                  }
                }
              }
            }
          }

          @media (${mq.desktop}) {
            .pre-footer {
              &__inner {
                display: flex;
                justify-content: center;
              }

              &__content {
                max-width: calc((100% - 2rem * 11) / 12 * 6 + 2rem * 5);
                margin-left: calc((100% - 2rem * 11) / 12 + 2rem);
                text-align: start;
              }

              &__ctas {
                margin-right: calc((100% - 2rem * 11) / 12 + 2rem);
              }
            }
          }

          @media (min-width: 77.5em) {
            .pre-footer {
              margin-left: auto;
              margin-right: auto;

              :global(.email-form) {
                width: 28.75rem;
              }
            }
          }
        `}
      </style>
    </>
  )
}
